import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { getCookie, setCookie } from './inboundHelper';

import { getEventDetails } from '@store/slices/eventSlice';
import { generatePassword, safeJSONParse } from './../fn';

export const useInbound = () => {
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const [inboundCookie, setInboundCookie] = useState<string | undefined>();

  useEffect(() => {
    const inbCookie = getCookie(`event-${sluggedEvent?.slug}-inbound`);
    setInboundCookie(inbCookie || undefined);
  }, [sluggedEvent?.slug]);

  const parsedInbound = useMemo(
    () => safeJSONParse(inboundCookie),
    [inboundCookie]
  );

  const isValidInboundForEvent = useMemo(() => {
    if (!parsedInbound || !sluggedEvent?.id) return false;
    const cookieEvent = String(parsedInbound?.event ?? '');
    return cookieEvent.length > 0 && cookieEvent === String(sluggedEvent.id);
  }, [parsedInbound, sluggedEvent?.id]);

  const shouldShowForm = useMemo(() => {
    return !inboundCookie || !isValidInboundForEvent;
  }, [inboundCookie, isValidInboundForEvent]);

  const updateInboundCookie = useCallback(
    (data?: Record<string, string>) => {
      if (!data?.event || !data || !sluggedEvent?.slug) return;

      const cookieData = {
        ...data,
      };

      const cookieName = `event-${sluggedEvent?.slug}-inbound`;
      const cookieValue = JSON.stringify(cookieData);

      setInboundCookie(cookieValue);
      setCookie(cookieName, cookieValue, 30);
    },
    [sluggedEvent?.slug]
  );

  const registerInbound = useCallback(
    async (data: Record<string, string>, callback?: () => void) => {
      const formData = new URLSearchParams();
      formData.append('event', data.event);
      formData.append('path', data.path);
      formData.append('action', data.action);
      // ------------------------------------
      formData.append(
        'address_country',
        data?.address_country?.toUpperCase() ?? ''
      );
      formData.append('company_name', data?.company_name ?? '');
      formData.append('contact_phone', data?.contact_phone ?? '');
      formData.append('contact_mobile', data?.contact_mobile ?? '');
      formData.append('email', data?.email ?? '');
      formData.append('first_name', data?.first_name ?? '');
      formData.append('last_name', data?.last_name ?? '');
      formData.append('job_title', data?.job_title ?? '');
      formData.append('salutation', data?.salutation ?? '');
      // ------------------------------------
      formData.append('company_industry', data?.company_industry ?? '');
      formData.append(
        'company_type_of_business',
        data?.company_type_of_business ?? ''
      );
      // booleans -> "true"/"false"
      formData.append('icebreaker', String(Boolean(data?.icebreaker)));
      formData.append('dinner', String(Boolean(data?.dinner)));

      formData.append('contact_type', String(data?.contact_type ?? 'del'));
      formData.append(
        'booking_package',
        String(data?.booking_package ?? 'OnsiteStaff')
      );

      const password = await generatePassword(data.email);

      await fetch(`/api/inbound/register`, {
        method: 'POST',
        headers: {
          Authorization: password,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      })
        .then(async response => await response.json())
        .then(data => {
          if (data?.errors) {
            console.error('Inbound registration errors:', data.errors);
          } else {
            const newInboundData = {
              ...(data?.data ?? {}),
              ...(data?.data?.client ?? {}),
            };
            delete newInboundData.client;
            updateInboundCookie(newInboundData);

            // Fire custom event for inbound registration completion
            const inboundRegistrationEvent = new CustomEvent(
              'inboundRegistrationComplete',
              {
                detail: newInboundData,
              }
            );
            window.dispatchEvent(inboundRegistrationEvent);

            if (callback) {
              callback();
            }
          }
        })
        .catch(e => {
          console.log('registerInbound error = ', e);
        });
    },
    [updateInboundCookie]
  );

  const actionInbound = useCallback(
    async (
      data: Record<string, string | number | boolean | undefined>,
      callback?: () => void,
      endpoint = '/api/inbound/action'
    ) => {
      const formData = new URLSearchParams();
      formData.append('event', String(data.event ?? ''));
      formData.append('path', String(data.path ?? ''));
      formData.append('action', String(data.action ?? ''));
      // ------------------------------------
      formData.append(
        'address_country',
        String(
          (data?.address_country as string | undefined)?.toUpperCase() ?? ''
        )
      );
      formData.append('company_name', String(data?.company_name ?? ''));
      formData.append('contact_phone', String(data?.contact_phone ?? ''));
      formData.append('contact_mobile', String(data?.contact_mobile ?? ''));
      formData.append('email', String(data?.email ?? ''));
      formData.append('first_name', String(data?.first_name ?? ''));
      formData.append('job_title', String(data?.job_title ?? ''));
      formData.append('last_name', String(data?.last_name ?? ''));
      formData.append('salutation', String(data?.salutation ?? ''));
      // ------------------------------------
      formData.append('company_industry', String(data?.company_industry ?? ''));
      formData.append(
        'company_type_of_business',
        String(data?.company_type_of_business ?? '')
      );
      formData.append('icebreaker', String(Boolean(data?.icebreaker)));
      formData.append('dinner', String(Boolean(data?.dinner)));
      formData.append('contact_type', String(data?.contact_type ?? 'del'));
      formData.append(
        'booking_package',
        String(data?.booking_package ?? 'OnsiteStaff')
      );

      const password = await generatePassword(String(data?.email ?? ''));

      fetch(`${endpoint}`, {
        method: 'POST',
        headers: {
          Authorization: password,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      })
        .then(async response => await response.json())
        .then(data => {
          if (data?.errors) {
            console.error('Inbound action errors:', data.errors);
          } else {
            const newInboundData = {
              ...(data?.data ?? {}),
              ...(data?.data?.client ?? {}),
            };
            delete newInboundData.client;
            updateInboundCookie(newInboundData);

            if (callback) {
              callback();
            }
          }
        })
        .catch(e => {
          console.log('actionInbound error = ', e);
        });
    },
    [updateInboundCookie]
  );

  return {
    inboundCookie,
    updateInboundCookie,
    actionInbound,
    registerInbound,
    email: safeJSONParse(inboundCookie)?.email,
    shouldShowForm,
    isValidInboundForEvent,
    parsedInbound,
  };
};
